export type KnobControlType = 'text' | 'number' | 'boolean' | 'select' | 'stringArray';

export interface KnobDef {
  name: string;
  controlType: KnobControlType;
  description?: string;
  required: boolean;
  enumOptions?: string[];
}

export interface SlotDef {
  name: string;
  description?: string;
}

/** Identity fields on a composition's generated JSON-Schema entry — not authorable props. */
const RESERVED_PROPERTY_NAMES = new Set(['id', 'component']);

function knobControlTypeFor(def: Record<string, unknown>, enumOptions?: string[]): KnobControlType | null {
  if (enumOptions) return 'select';
  switch (def.type) {
    case 'string':
      return 'text';
    case 'number':
      return 'number';
    case 'boolean':
      return 'boolean';
    case 'array':
      // Slot properties are emitted as bare `{ type: 'array', description }` with no `items`
      // (see `compositionInterfaceToJsonSchema`) — a knob can't usefully edit a child-component
      // list, so only `string[]`-typed props (which carry `items: { type: 'string' }`) knob.
      return (def.items as { type?: string } | undefined)?.type === 'string' ? 'stringArray' : null;
    default:
      return null;
  }
}

/**
 * Generates one knob per composition-interface property from a composition's stored
 * `interfaceSchema` (the JSON-Schema fragment `compositionInterfaceToJsonSchema` produces at
 * publish time — see `getComposition.ts`'s `interfaceSchema` field). Powers the Explorer story
 * panel's knob controls (SC6): each declared prop yields a control matching its type/enum.
 */
export function generateKnobsFromInterfaceSchema(
  schema: Record<string, unknown> | null | undefined
): KnobDef[] {
  if (!schema || typeof schema !== 'object') return [];
  const properties = (schema.properties ?? {}) as Record<string, Record<string, unknown>>;
  const required = new Set(((schema.required as string[] | undefined) ?? []));

  const knobs: KnobDef[] = [];
  for (const [name, def] of Object.entries(properties)) {
    if (RESERVED_PROPERTY_NAMES.has(name)) continue;
    const enumOptions = Array.isArray(def.enum) ? (def.enum as string[]) : undefined;
    const controlType = knobControlTypeFor(def, enumOptions);
    if (!controlType) continue;
    knobs.push({
      name,
      controlType,
      description: typeof def.description === 'string' ? def.description : undefined,
      required: required.has(name),
      enumOptions,
    });
  }
  return knobs;
}

/**
 * Generates one slot entry per composition-interface slot from a composition's stored
 * `interfaceSchema`. `compositionInterfaceToJsonSchema` renders each declared slot as a bare
 * `{ type: 'array', description }` property with no `items` (unlike a `string[]` prop, which
 * always carries `items: { type: 'string' }`) — that absence is the only signal left once the
 * interface has round-tripped through JSON Schema, so it doubles as the slot/prop discriminator
 * here. Powers the Explorer docs panel's Slots section (SC6).
 */
export function extractSlotsFromInterfaceSchema(schema: Record<string, unknown> | null | undefined): SlotDef[] {
  if (!schema || typeof schema !== 'object') return [];
  const properties = (schema.properties ?? {}) as Record<string, Record<string, unknown>>;

  const slots: SlotDef[] = [];
  for (const [name, def] of Object.entries(properties)) {
    if (RESERVED_PROPERTY_NAMES.has(name)) continue;
    if (def.type !== 'array' || def.items !== undefined) continue;
    slots.push({
      name,
      description: typeof def.description === 'string' ? def.description : undefined,
    });
  }
  return slots;
}
